import { createContext, ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Where the analytics dashboard runs: the global Mundu dashboard, or one organization's
 * Analytics module, which shows only that organization and opens links directly.
 */
export type AnalyticsScope =
    | { kind: 'global' }
    | {
          kind: 'organization';
          organizationId: string;
          /** Clickable figures open their lists directly; administrators only for now. */
          canOpenLinks: boolean;
      };

const AnalyticsScopeContext = createContext<AnalyticsScope>({ kind: 'global' });

export const AnalyticsScopeProvider = ({
    scope,
    children,
}: {
    scope: AnalyticsScope;
    children: ReactNode;
}) => (
    <AnalyticsScopeContext.Provider value={scope}>
        {children}
    </AnalyticsScopeContext.Provider>
);

export const useAnalyticsScope = () => useContext(AnalyticsScopeContext);

/**
 * Translations with the organization wording where it exists (`key_organization`), falling back
 * to the shared text. Used for notes that otherwise mention "selected organizations".
 */
export const useScopedAnalyticsText = () => {
    const { t } = useTranslation();
    const scope = useAnalyticsScope();
    const context = scope.kind === 'organization' ? 'organization' : undefined;
    return (key: string, options?: Record<string, unknown>) =>
        t(key, { ...options, context });
};
